<script setup>
// core
import {onMounted, ref, getCurrentInstance, computed} from "vue";
import {useStore} from 'vuex'
import {useI18n} from "vue-i18n";
import {useWindowSize} from '@vueuse/core'


// components
import StatisticCard from "@/components/Telephony/StatisticCard.vue";
import BaseSearchInput from "@/components/ui/BaseSearchInput.vue";
import CallsHistoryTable from "@/components/Telephony/CallsHistoryTable.vue";
import InfoDataTextRecurring from "@/components/Recurring/InfoDataTextRecurring.vue";
import CallsHistoryMobileTable from "@/components/Telephony/CallsHistoryMobileTable.vue";
import CallsHistoryNarrowTable from "@/components/Telephony/CallsHistoryNarrowTable.vue";
import CallsEmptyState from "@/components/Telephony/CallsEmptyState.vue";
import BasePagination from "@/components/ui/BasePagination.vue";

// ---
const {width} = useWindowSize()
const {t: $t} = useI18n()
const {proxy} = getCurrentInstance()
const store = useStore()
const isStatisticsLoading = ref(false)
const isSearchLoading = ref(false)
const isTableLoading = ref(false)
const searchQ = ref('')

// computed
const statisticsCards = computed(() => {
    const cards = store.getters['telephony/getCards']
    return Object.entries(cards).map(([key, value]) => ({
        key,
        count: value,
        description: $t(`telephony.statistics.cards.${key}`) === `telephony.statistics.cards.${key}`
            ? key
            : $t(`telephony.statistics.cards.${key}`)
    }));
})

const callsHistory = computed(() => store.getters['telephony/getCallsHistory'])
const historyMeta = computed(() => store.getters['telephony/getCallsMeta'])

// a search with no hits keeps the table: the empty state is for an account
// that has no calls at all
const isEmpty = computed(() =>
    !callsHistory.value?.length && !isTableLoading.value && !searchQ.value
)

// methods
const onSearchApply = async () => {
    await loadCallsHistory(1, searchQ.value)
}

const loadStatistics = async () => {
    isStatisticsLoading.value = true
    try {
        await store.dispatch('telephony/getStatisticCards', proxy.$moshaToast)
    } finally {
        isStatisticsLoading.value = false
    }
}

const loadCallsHistory = async (page = 1, search = undefined, append = false) => {
    isTableLoading.value = true

    // the store keeps the calls between visits; like the messenger tables, a fresh
    // load starts empty so the loader takes the rows' place instead of hanging under them
    if (!append) {
        isSearchLoading.value = true
        store.commit('telephony/SET_HISTORY_DATA', {data: null, append: false})
    }

    try {
        await store.dispatch('telephony/getCallsHistory', {
            query: {
                page: search ? 1 : page,
                number: !!search ? search : undefined
            },
            moshaToast: proxy.$moshaToast,
            append
        })
    } finally {
        isTableLoading.value = false
        isSearchLoading.value = false
    }
}

const loadMoreHistory = async () => {
    const currentPage = historyMeta.value.current_page || 1
    const totalPages = historyMeta.value.last_page || 1

    if (currentPage < totalPages && !isTableLoading.value) {
        await loadCallsHistory(currentPage + 1, searchQ.value, true)
    }
}

// hooks
onMounted(async () => {
    await Promise.allSettled([
        loadCallsHistory(),
        loadStatistics()
    ])
})
</script>

<template>
    <section class="telephony">
        <div class="telephony__statistics">
            <StatisticCard
                v-for="(card, i) in statisticsCards"
                :key="i"
                :loading="isStatisticsLoading"
                :card="card"
            />
        </div>

        <div class="telephony__table">
            <div class="telephony__tableHead">
                <h2 class="telephony__title">{{ $t('telephony.calls_history.title') }}</h2>
                <BaseSearchInput v-model="searchQ" numeric-only :loading="isSearchLoading" @submit-search="onSearchApply"/>
            </div>

            <!-- seven columns need 1200px; 900–1199 gets five, narrower ones get cards -->
            <component
                :is="width >= 1200 ? CallsHistoryTable : CallsHistoryNarrowTable"
                v-if="width >= 900"
                :data="callsHistory"
                :loading="isTableLoading"
                @load-more="loadMoreHistory"
            >
                <template #information_column>
                    <InfoDataTextRecurring
                        v-if="isTableLoading"
                        value="loading"
                        height="120px"
                    />

                    <InfoDataTextRecurring
                        v-else-if="!callsHistory?.length && !isEmpty"
                        value="noData"
                        height="120px"
                    />
                </template>
            </component>

            <template v-else>
                <InfoDataTextRecurring
                    v-if="isTableLoading"
                    value="loading"
                    height="120px"
                />

                <CallsEmptyState v-else-if="isEmpty"/>

                <InfoDataTextRecurring
                    v-else-if="!callsHistory?.length"
                    value="noData"
                    height="120px"
                />

                <CallsHistoryMobileTable v-else :data="callsHistory"/>

                <BasePagination
                    class=""
                    :lastPage="historyMeta.last_page"
                    :currentPage="historyMeta.current_page"
                    @onChangePage="(numberPage) => loadCallsHistory(numberPage, searchQ)"
                />
            </template>

            <!-- outside the table: its body scrolls at 293px and highlights rows on hover -->
            <CallsEmptyState v-if="width >= 900 && !isTableLoading && isEmpty"/>
        </div>
    </section>
</template>

<style scoped lang="scss">
.telephony {
    padding: 20px;
    display: flex;
    flex-direction: column;
    gap: 30px;

    &__statistics {
        display: flex;
        align-items: stretch;
        gap: 17px;
        flex-wrap: wrap;
    }

    &__table {
        display: flex;
        flex-direction: column;
        gap: 12px;
    }

    &__tableHead {
        display: flex;
        align-items: center;
        gap: 8px;

        @media (max-width: 480px) {
            flex-direction: column;
            align-items: flex-start;
        }
    }

    &__title {
        flex: 1;
        color: #404040;
        font-family: Gilroy;
        font-size: 24px;
        font-weight: 500;
        text-transform: uppercase;
    }
}
</style>
